import React, {useEffect, useMemo, useState} from "react";
import {useObserver} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {Button, Col, message, Row, Select, Skeleton, Spin, Tooltip} from "antd";
import {WarningOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {useHistory} from "react-router-dom";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {IconCol, SelectField} from "./EditPoolCouriers.styles";
import {CourierIcon} from "../../_defaultComponents/CourierIcon/CourierIcon";
import {arrayAlphabeticalSort, formatPhoneNumber} from "../../../utils/helpers";
import debounce from "lodash/debounce";
import moment from "moment";

const EditPoolCouriers = ({poolId}) => {
    const {t} = useTranslation([]);
    const stores = useStore();
    const history = useHistory();

    const [checkCourierLoading, setCheckCourierLoading] = useState(false);
    const [searchText, setSearchText] = useState();

    const [selectedCouriers, setSelectedCouriers] = useState([]);
    const [courierOptions, setCourierOptions] = useState([]);
    const [loadingCouriers] = useFetch({
        fetchFn: stores.courierStore.getCourierList.bind(null, {
            limit: 10,
            skip: 0,
            statuses: [],
            types: [],
            searchText,
            city: "all"
        }),
        defaultDataValue: {rows: []},
        dependencies: [searchText],
        initialCall: true,
        callbackFunc: data => {
            const oldCourierOptions = [...courierOptions];
            data.rows
                .filter(
                    courier =>
                        oldCourierOptions.findIndex(
                            oldCourier => oldCourier._id === courier._id
                        ) === -1
                )
                .forEach(courier => {
                    oldCourierOptions.push(courier);
                });
            setCourierOptions([...oldCourierOptions]);
        }
    });
    const debounceTimeout = 400;
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [courierErrors, setCourierErrors] = useState({});

    const [loading, pool] = useFetch({
        fetchFn: stores.poolStore.getPool.bind(null, {poolId}),
        initialCall: true,
        defaultDataValue: undefined
    });

    const initSelectedCouriers = ({courierIds}) => {
        setSelectedCouriers(prev => [
            ...prev,
            ...courierIds.filter(
                courierId => prev.findIndex(prevCourierId => prevCourierId === courierId) === -1
            )
        ]);
    };
    const renderCourierOptions = () => {
        {
            const couriers = [...courierOptions];
            if (pool && pool?.couriers) {
                pool.couriers
                    .filter(c => couriers.findIndex(courier => courier._id === c._id) === -1)
                    .forEach(courier => {
                        couriers.push(courier);
                    });
            }
            couriers.forEach(courier => {
                if (Boolean(courierErrors?.[courier._id])) {
                    courier.hasError = true;
                    courier.error = {
                        reason: courierErrors[courier._id].reason
                    };
                } else {
                    courier.hasError = false;
                    courier.error = undefined;
                }
            });

            return arrayAlphabeticalSort([...couriers], "fullname").map(courier => {
                return (
                    <SelectField.Option
                        key={courier.fullname}
                        value={courier._id}
                        data-testid="companyCourierSelect_option"
                        disabled={checkCourierLoading}
                        data-tckn={courier.tckn}
                        data-cellPhone={courier.cellPhone}
                    >
                        <Tooltip title={courier?.error?.reason || ""}>
                            <Row>
                                <IconCol hasError={courier?.hasError}>
                                    {courier?.hasError ? (
                                        <WarningOutlined style={{marginBottom: "5px"}} />
                                    ) : (
                                        <CourierIcon courierType={courier.type} />
                                    )}
                                </IconCol>
                                <Col>
                                    <Row style={{alignItems: "center"}}>
                                        {courier.fullname}
                                        {courier?.hasError ? (
                                            <InfoCircleOutlined style={{marginLeft: "5px"}} />
                                        ) : (
                                            <></>
                                        )}
                                    </Row>
                                    <Row>{formatPhoneNumber(courier.cellPhone)}</Row>
                                </Col>
                            </Row>
                        </Tooltip>
                    </SelectField.Option>
                );
            });
        }
    };

    const handlePoolCouriersSubmit = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                poolId,
                name: pool.name,
                city: pool.city,
                companies: pool.companies.map(company => company._id),
                subCompanies: pool.subCompanies.map(subCompany => subCompany._id),
                couriers: selectedCouriers,
                isActive: pool.isActive
            };
            const result = await stores.poolStore.updatePool(payload);
            if (Boolean(result?.failed?.length > 0)) {
                const errorCouriers = {};
                result.failed.forEach(failedCourier => {
                    errorCouriers[failedCourier.courierId] = {
                        reason: failedCourier?.reason?.join(" ") || "-"
                    };
                });
                setCourierErrors(errorCouriers);
            } else {
                history.push(`/pools/${poolId}`);
            }
        } catch (err) {
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSearchCourier = useMemo(
        value => {
            const loadOptions = value => {
                setSearchText(value);
            };
            return debounce(loadOptions, debounceTimeout);
        },
        [debounceTimeout]
    );

    const handleChangeCouriers = async (value = []) => {
        setCheckCourierLoading(true);
        try {
            const oldCouriers = pool?.couriers?.map(courier => courier._id) || [];
            const newCouriers = value;

            const diffCouriers = {
                added: newCouriers.filter(
                    courier => oldCouriers.findIndex(oldCourier => oldCourier === courier) === -1
                ),
                removed: oldCouriers.filter(
                    courier => newCouriers.findIndex(newCourier => newCourier === courier) === -1
                )
            };

            const checkCouriersPoolShiftsResult = await stores.poolShiftStore.checkCouriersPoolShifts(
                {
                    poolId,
                    courierIds: diffCouriers.removed,
                    whereType: "in"
                }
            );
            if (!checkCouriersPoolShiftsResult) {
                message.error("Kuryenin vardiyaları kontrol edilemedi.");
            }

            const couriers = [...courierOptions];
            if (pool && pool?.couriers) {
                pool.couriers
                    .filter(c => couriers.findIndex(courier => courier._id === c._id) === -1)
                    .forEach(courier => {
                        couriers.push(courier);
                    });
            }
            const courierPoolShifts = checkCouriersPoolShiftsResult?.data || [];
            const hasShiftCouriers = courierPoolShifts.map(courier => ({
                ...couriers?.find(c => c?._id?.toString() == courier?._id?.toString()),
                poolShiftDates: courier?.dates
            }));

            for (const courier of hasShiftCouriers) {
                message.error(
                    <>
                        <b>{courier.fullname}</b> isimli kuryenin,{" "}
                        <b>
                            {courier.poolShiftDates
                                .map(d => (d ? moment(d, "YYYY-MM-DD").format("DD.MM.YYYY") : d))
                                .join(", ")}
                        </b>{" "}
                        tarihlerinde havuz vardiyası bulunmaktadır. Bu nedenle havuzdan çıkarılamaz.
                    </>,
                    10
                );
            }
            if (hasShiftCouriers.length > 0) {
                value.push(
                    ...diffCouriers.removed.filter(courier =>
                        Boolean(hasShiftCouriers?.find(c => c._id === courier))
                    )
                );
            }
            setSelectedCouriers(value);
        } catch (err) {
        } finally {
            setCheckCourierLoading(false);
        }
    };

    useEffect(() => {
        if (!pool?._id || loading) return;
        initSelectedCouriers({courierIds: pool.couriers.map(courier => courier._id)});
    }, [pool]);

    return useObserver(() => (
        <Row type="flex" justify="center" data-testid="editPoolScreen" gutter={[0, 20]}>
            {loading && pool?._id ? (
                <Skeleton paragraph={{rows: 2, width: "100%"}}></Skeleton>
            ) : (
                <>
                    <Col span={20}>
                        <h1>{t("screens.editPoolCouriers", {ns: "pools"})}</h1>
                        <Col span={24} data-testid="poolCouriers">
                            <h4>{t("fields.poolCouriers", {ns: "pools"})}</h4>
                            <SelectField
                                notFoundContent={loadingCouriers ? <Spin size="small" /> : null}
                                allowClear
                                style={{width: "100%"}}
                                value={selectedCouriers}
                                data-testid="poolCourierSelect"
                                onChange={value => {
                                    handleChangeCouriers(value);
                                }}
                                onSearch={onSearchCourier}
                                loading={loadingCouriers || checkCourierLoading}
                                disabled={checkCourierLoading}
                                showSearch
                                mode="multiple"
                                filterOption={(input, option) => {
                                    const tckn = option.props["data-tckn"] || "";
                                    const cellPhone = option.props["data-cellPhone"] || "";
                                    return (
                                        option.key.toLowerCase().indexOf(input.toLowerCase()) >=
                                            0 ||
                                        tckn
                                            .toString()
                                            .toLowerCase()
                                            .indexOf(input.toLowerCase()) >= 0 ||
                                        cellPhone
                                            .toString()
                                            .toLowerCase()
                                            .indexOf(input.toLowerCase()) >= 0
                                    );
                                }}
                            >
                                {renderCourierOptions()}
                            </SelectField>
                        </Col>
                        <Col
                            span="24"
                            style={{display: "flex", justifyContent: "flex-end", marginTop: "10px"}}
                        >
                            <Button
                                type="primary"
                                htmlType="submit"
                                disabled={isSubmitting}
                                onClick={handlePoolCouriersSubmit}
                            >
                                {t("actions.update", {ns: "common"})}
                            </Button>
                        </Col>
                    </Col>
                </>
            )}
        </Row>
    ));
};

export default EditPoolCouriers;
